import React, { useState } from "react";
import { auditApi } from "../services/api";

interface FieldDef {
  key: string;
  label: string;
  kind: "text" | "date" | "textarea" | "select";
  options?: string[];
}

interface GroupDef {
  id: string;
  title: string;
  fields: FieldDef[];
}

interface AdmissionRecord {
  id: string;
  savedAt: string;
  savedBy: string;
  data: Record<string, string>;
  evidence: string[];
}

const STORE_KEY = "admission_records";

const f = (key: string, label: string, kind: "text" | "date" | "textarea" | "select" = "text", options?: string[]): FieldDef => ({ key, label, kind, options });

const GROUPS: GroupDef[] = [
  { id: "personal", title: "Personal Details", fields: [
    f("fullName", "Full name"),
    f("dob", "Date of birth", "date"),
    f("gender", "Gender", "select", ["Female", "Male", "Other", "Prefer not to say"]),
    f("nhsNumber", "NHS number"),
    f("room", "Room number"),
    f("admissionDate", "Admission date", "date"),
    f("admissionTime", "Admission time"),
    f("admittedFrom", "Admitted from", "select", ["Own home", "Hospital", "Another care home", "Respite", "Other"]),
    f("admittedFromDetails", "If hospital or other, give name and details"),
    f("reason", "Reason for admission", "textarea"),
    f("gpName", "GP name"),
    f("gpSurgery", "GP surgery and phone"),
    f("pharmacy", "Pharmacy"),
    f("religion", "Religion or belief"),
    f("language", "Preferred language"),
    f("keyWorker", "Key worker assigned"),
  ]},
  { id: "contacts", title: "Contacts and Professionals", fields: [
    f("nokName", "Next of kin name and relationship"),
    f("nokPhone", "Next of kin phone and email"),
    f("otherContact", "Other important contacts"),
    f("socialWorker", "Social worker and team"),
    f("districtNurse", "District nurse or community team"),
    f("otherProfessionals", "Other professionals (OT, SALT, physio)"),
  ]},
  { id: "funding", title: "Funding", fields: [
    f("payer", "Who pays", "select", ["Self funded", "Local authority", "NHS continuing healthcare", "Mixed"]),
    f("fundingDetails", "Funding details and reference"),
  ]},
  { id: "clinical", title: "Clinical on Admission", fields: [
    f("weight", "Weight kg"),
    f("height", "Height cm"),
    f("must", "MUST score"),
    f("skin", "Skin integrity and any wounds"),
    f("pressureScore", "Pressure risk score"),
    f("mobility", "Mobility and aids"),
    f("fallsScore", "Falls risk score"),
    f("continence", "Continence"),
    f("capacity", "Mental capacity status"),
    f("dnacr", "DNACPR in place", "select", ["Yes", "No", "Unknown"]),
    f("allergies", "Allergies"),
    f("diet", "Dietary needs and texture"),
    f("communication", "Communication needs"),
    f("sleep", "Sleep pattern"),
    f("behaviour", "Behaviour or psychological needs"),
  ]},
  { id: "meds", title: "Medications on Admission", fields: [
    f("medList", "Current medications and doses", "textarea"),
    f("marReceived", "MAR received", "select", ["Yes", "No"]),
    f("cdBalance", "Controlled drugs balance on transfer"),
    f("medsNotes", "Medication notes (PRN, creams, eye drops)"),
  ]},
  { id: "care", title: "Care Planning, Risks and Consents", fields: [
    f("carePlans", "Care plans started (list)", "textarea"),
    f("riskAssessments", "Risk assessments completed (falls, pressure, nutrition, moving handling)", "textarea"),
    f("consents", "Consents signed (care, treatment, photos, data)", "textarea"),
    f("dols", "DoLS or standard authorisation status"),
    f("property", "Property and valuables inventory signed", "select", ["Yes", "No"]),
    f("belongings", "Personal belongings notes"),
  ]},
  { id: "lifestyle", title: "Lifestyle and Preferences", fields: [
    f("occupation", "Previous occupation and skills"),
    f("interests", "Interests and hobbies"),
    f("routines", "Daily routines and preferences"),
    f("likes", "Likes and dislikes"),
    f("cultural", "Cultural and spiritual needs"),
  ]},
];

function loadHistory(): AdmissionRecord[] {
  try {
    return JSON.parse(localStorage.getItem(STORE_KEY) || "[]");
  } catch (error) {
    return [];
  }
}
export default function Admissions({ user }: { user?: any }) {
  const [form, setForm] = useState<Record<string, string>>({});
  const [history, setHistory] = useState<AdmissionRecord[]>(loadHistory);
  const [showHistory, setShowHistory] = useState(false);
  const [evidence, setEvidence] = useState<string[]>([]);
  const [message, setMessage] = useState("");
  const [editingId, setEditingId] = useState("");

  const set = (key: string, value: string) => setForm(current => ({ ...current, [key]: value }));

  const attach = async (file: File) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("kind", file.type.startsWith("video") ? "video" : "photo");
      formData.append("context", "admission");
      await auditApi.uploadEvidence(formData);
      setEvidence(current => [...current, `${file.name} (uploaded)`]);
    } catch (error) {
      setEvidence(current => [...current, `${file.name} (saved locally)`]);
    }
  };

  const saveAdmission = () => {
    if (!form.fullName) {
      setMessage("Full name is required.");
      return;
    }

    const record: AdmissionRecord = {
      id: editingId || `adm-${Date.now()}`,
      savedAt: new Date().toISOString(),
      savedBy: user && user.name ? user.name : "Unknown",
      data: form,
      evidence,
    };

    const rest = history.filter(item => item.id !== record.id);
    const next = [record, ...rest];

    setHistory(next);
    localStorage.setItem(STORE_KEY, JSON.stringify(next));
    setMessage(`Admission for ${form.fullName} saved.`);
  };

  const loadRecord = (record: AdmissionRecord) => {
    setForm(record.data);
    setEvidence(record.evidence);
    setEditingId(record.id);
    setShowHistory(false);
  };

  const newAdmission = () => {
    setForm({});
    setEvidence([]);
    setEditingId("");
    setShowHistory(false);
    setMessage("");
  };

  const renderField = (field: FieldDef) => {
    const value = form[field.key] || "";

    if (field.kind === "textarea") {
      return <textarea value={value} onChange={event => set(field.key, event.target.value)} rows={3} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />;
    }

    if (field.kind === "select") {
      return (
        <select value={value} onChange={event => set(field.key, event.target.value)} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm">
          <option value=""></option>
          {(field.options || []).map(option => <option key={option}>{option}</option>)}
        </select>
      );
    }

    return <input type={field.kind === "date" ? "date" : "text"} value={value} onChange={event => set(field.key, event.target.value)} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />;
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Admissions</h2>
          <p className="text-sm text-slate-600">Comprehensive admission record covering personal details, contacts, funding, clinical needs, medications, care planning, consents and lifestyle.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={newAdmission} className="rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white">New admission</button>
          <button onClick={() => setShowHistory(!showHistory)} className="rounded-lg border border-blue-900 px-4 py-2 text-sm font-medium text-blue-900">
            {showHistory ? "Back to form" : `Records (${history.length})`}
          </button>
        </div>
      </div>

      {message ? <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">{message}</div> : null}

      {showHistory ? (
        <div className="space-y-3">
          {history.map(record => (
            <div key={record.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div>
                <div className="font-medium text-slate-900">{record.data.fullName || "Unnamed resident"}</div>
                <div className="text-xs text-slate-500">
                  Room {record.data.room || "-"} | Admitted {record.data.admissionDate || "-"} from {record.data.admittedFrom || "-"}
                </div>
              </div>
              <button onClick={() => loadRecord(record)} className="rounded-lg border border-slate-300 px-3 py-1 text-sm">Open</button>
            </div>
          ))}
          {history.length === 0 ? <div className="text-sm text-slate-500">No admissions recorded yet.</div> : null}
        </div>
      ) : (
        <div className="space-y-4">
          {GROUPS.map(group => (
            <div key={group.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="mb-3 font-semibold text-slate-900">{group.title}</h3>
              <div className="grid gap-3 md:grid-cols-2">
                {group.fields.map(field => (
                  <div key={field.key} className={field.kind === "textarea" ? "md:col-span-2" : ""}>
                    <label className="mb-1 block text-xs font-medium text-slate-600">{field.label}</label>
                    {renderField(field)}
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="mb-2 font-semibold text-slate-900">Documents and Photos</h3>
            <div className="flex flex-wrap gap-2">
              <label className="cursor-pointer rounded-lg border border-slate-300 px-3 py-1 text-sm">
                Add photo
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={event => { const file = event.target.files && event.target.files[0]; if (file) attach(file); }} />
              </label>
              <label className="cursor-pointer rounded-lg border border-slate-300 px-3 py-1 text-sm">
                Add video
                <input type="file" accept="video/*" className="hidden" onChange={event => { const file = event.target.files && event.target.files[0]; if (file) attach(file); }} />
              </label>
            </div>
            {evidence.length > 0 ? <div className="mt-2 text-xs text-slate-500">{evidence.join(", ")}</div> : null}
          </div>

          <button onClick={saveAdmission} className="rounded-lg bg-blue-900 px-5 py-2 text-sm font-medium text-white">
            Save admission record
          </button>
        </div>
      )}
    </div>
  );
}
